// Turning a store plan into "open the filled cart" for each store.
// Amazon takes one link that fills the cart; Costco Same-Day has none, so each line opens
// its product page (or a search) to add by hand.

import type { StorePlan } from '@shared/routing.ts';
import { amazonSearchUrl, buildAmazonCartUrl, isValidAsin } from '@shared/amazon.ts';
import { costcoProductUrl, costcoSameDaySearchUrl } from '@shared/costco.ts';
import { config } from '../../config.ts';

export interface ChecklistEntry {
  name: string;
  qty: number;
  url: string;
  /** True when `url` is the matched product's page rather than a search. */
  product: boolean;
}

export interface CartOpenResult {
  /** One URL that fills the cart (when the store supports it). */
  url?: string;
  /** Items that must be added by hand (no product match or no link support). */
  checklist: ChecklistEntry[];
  note?: string;
}

/** Stores with a link that fills the cart; the rest are a checklist. */
export function hasCartLink(storeId: StorePlan['storeId']): boolean {
  return storeId === 'amazon' || storeId === 'amazonfresh';
}

function entry(plan: StorePlan, line: StorePlan['lines'][number]): ChecklistEntry {
  const query = line.product.title || line.item.name;
  if (plan.storeId === 'costco') {
    const page = costcoProductUrl(line.product.productId);
    return { name: line.item.name, qty: line.item.qty, url: page ?? costcoSameDaySearchUrl(query), product: !!page };
  }
  const prefix = plan.storeId === 'wholefoods' ? 'whole foods ' : '';
  return { name: line.item.name, qty: line.item.qty, url: amazonSearchUrl(prefix + query, { fresh: plan.storeId === 'amazonfresh' }), product: false };
}

export function prepareCart(plan: StorePlan): CartOpenResult {
  if (hasCartLink(plan.storeId)) {
    const fresh = plan.storeId === 'amazonfresh';
    const linked = plan.lines.filter((l) => isValidAsin(l.product.productId));
    const manual = plan.lines.filter((l) => !isValidAsin(l.product.productId));
    return {
      url: linked.length
        ? buildAmazonCartUrl(
            linked.map((l) => ({ asin: l.product.productId, qty: l.item.qty })),
            config.amazonAssociateTag || undefined,
            { fresh },
          )
        : undefined,
      checklist: manual.map((l) => entry(plan, l)),
    };
  }
  const note = plan.storeId === 'costco' ? 'Tap each to add it to the cart:' : 'This store has no cart link. Add these by hand:';
  return { checklist: plan.lines.map((l) => entry(plan, l)), note };
}
